import { MailIcon, OfficeBuildingIcon } from '@heroicons/react/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { CardTitle } from 'shared-ui/lib/components/CardTitle';
import { GenericIndexScene } from 'shared-ui/lib/components/table/TableX';
import { setupQueryConfig } from 'shared-ui/lib/config/query.config';
import { GenericTable } from 'shared-ui/lib/customs/react/generic-table/GenericTable';
import { partnerCustomerInviteColumns } from '../../columns/partner-customer-invite.columns';
import { partnerCustomerColumns } from '../../columns/partner-customer.columns';
import { ConfirmModal } from '../../components/ConfirmModal';
import { InviteCustomerForm } from '../../form/invite-customer/InviteCustomerForm';
import { InviteCustomerFormBody } from '../../form/invite-customer/invite-customer.form';
import { useCan } from '../../hooks/use-can';
import {
  PartnerCustomerInvitesQuery,
  PartnerCustomersQuery,
  useCancelPartnerCustomerInviteMutation,
  useEndPartnerCustomerMutation,
  useInvitePartnerCustomerMutation,
  usePartnerCustomerInvitesQuery,
  usePartnerCustomersLazyQuery,
} from '../../generated/graphql';

type CustomersTab = 'customers' | 'invites';
type CustomerRow = PartnerCustomersQuery['partnerCustomers']['list'][0];
type InviteRow = PartnerCustomerInvitesQuery['partnerCustomerInvites'][0];

const tabClassName = (active: boolean): string =>
  `appearance-none flex-1 inline-flex items-center justify-center px-6 py-3 text-base font-semibold border-0 border-b-4 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 ${
    active
      ? 'bg-white text-primary border-primary'
      : 'bg-gray-100 text-gray-color border-transparent hover:bg-gray-200 hover:text-gray-900'
  }`;

const tableClassNames =
  'bg-white min-w-full divide-y divide-gray-200 overflow-hidden border-b border-gray-200 rounded-lg';

export const CustomersScene = (): JSX.Element => {
  const { t } = useTranslation('ServiceCallCustomersScene');
  const { canManageUsers, isMaster } = useCan();
  const [tab, setTab] = useState<CustomersTab>('customers');
  const [customerToEnd, setCustomerToEnd] = useState<CustomerRow | null>(null);
  const [inviteToCancel, setInviteToCancel] = useState<InviteRow | null>(null);

  const customersQuery = usePartnerCustomersLazyQuery(setupQueryConfig());
  const invitesQuery = usePartnerCustomerInvitesQuery(setupQueryConfig());
  const [inviteCustomer, { loading: inviteLoading }] = useInvitePartnerCustomerMutation();
  const [endCustomer] = useEndPartnerCustomerMutation();
  const [cancelInvite] = useCancelPartnerCustomerInviteMutation();

  const inviteCount = invitesQuery.data?.partnerCustomerInvites.length ?? 0;

  const handleInvite = async (body: InviteCustomerFormBody): Promise<void> => {
    const result = await inviteCustomer({ variables: { body } });
    if (!result.data) return;
    toast.success(t('inviteSuccess'));
    await invitesQuery.refetch();
  };

  const handleConfirmEnd = async (): Promise<void> => {
    if (!customerToEnd) return;
    const result = await endCustomer({ variables: { id: customerToEnd.id } });
    setCustomerToEnd(null);
    if (!result.data) return;
    toast.success(t('endCustomerSuccess'));
    await customersQuery[1].refetch();
  };

  const handleConfirmCancel = async (): Promise<void> => {
    if (!inviteToCancel) return;
    const result = await cancelInvite({ variables: { id: inviteToCancel.id } });
    setInviteToCancel(null);
    if (!result.data) return;
    toast.success(t('cancelInviteSuccess'));
    await invitesQuery.refetch();
  };

  const customerColumns = [
    partnerCustomerColumns.businessName,
    partnerCustomerColumns.vehicleCount,
    partnerCustomerColumns.goToVehicles,
    partnerCustomerColumns.startedAt,
    ...(isMaster ? [partnerCustomerColumns.endCustomer(setCustomerToEnd)] : []),
  ];

  const inviteColumns = [
    partnerCustomerInviteColumns.businessName,
    partnerCustomerInviteColumns.email,
    partnerCustomerInviteColumns.createdAt,
    ...(canManageUsers ? [partnerCustomerInviteColumns.cancelInvite(setInviteToCancel)] : []),
  ];

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="flex border-b border-gray-200" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'customers'}
          className={tabClassName(tab === 'customers')}
          onClick={() => setTab('customers')}
        >
          <OfficeBuildingIcon className="h-5 w-5 mr-2 flex-shrink-0" aria-hidden="true" />
          {t('tabCustomers')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'invites'}
          className={tabClassName(tab === 'invites')}
          onClick={() => setTab('invites')}
        >
          <MailIcon className="h-5 w-5 mr-2 flex-shrink-0" aria-hidden="true" />
          <span>{t('tabInvites')}</span>
          {inviteCount > 0 ? (
            <span
              className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-primary text-white text-xs font-bold leading-none flex-shrink-0"
              aria-label={`${inviteCount} ${t('tabInvites')}`}
            >
              {inviteCount}
            </span>
          ) : undefined}
        </button>
      </div>
      <div className="p-4">
        <CardTitle>{tab === 'customers' ? t('tabCustomers') : t('tabInvites')}</CardTitle>
        {tab === 'customers' ? (
          <GenericIndexScene
            key="customers"
            unwrapped
            perPage={30}
            dataPath="partnerCustomers.list"
            countPath="partnerCustomers.totalCount"
            query={customersQuery}
            defaultOrderBy={{ startedAt: 'desc' }}
            columns={customerColumns}
          />
        ) : (
          <>
            {canManageUsers ? (
              <InviteCustomerForm loading={inviteLoading} onSubmit={handleInvite} />
            ) : undefined}
            {inviteCount === 0 ? (
              <p className="text-sm text-gray-color">{t('emptyInvites')}</p>
            ) : (
              <div className="overflow-x-auto">
                <GenericTable
                  columns={inviteColumns}
                  data={invitesQuery.data?.partnerCustomerInvites}
                  tableClassNames={tableClassNames}
                  tHeadClassNames="bg-gray-50"
                  thClassNames="px-6 py-3 text-left text-xs font-medium text-gray-color uppercase tracking-wider"
                  tdClassNames="px-6 py-4 text-sm font-medium"
                  trClassNames="even:bg-gray-50"
                />
              </div>
            )}
          </>
        )}
      </div>
      <ConfirmModal
        isOpen={Boolean(customerToEnd)}
        onCancel={() => setCustomerToEnd(null)}
        onConfirm={handleConfirmEnd}
        title={t('endCustomerConfirmTitle')}
        description={t('endCustomerConfirmDescription')}
        confirmLabel={t('endCustomerConfirm')}
        cancelLabel={t('confirmCancel')}
        variant="danger"
      />
      <ConfirmModal
        isOpen={Boolean(inviteToCancel)}
        onCancel={() => setInviteToCancel(null)}
        onConfirm={handleConfirmCancel}
        title={t('cancelInviteConfirmTitle')}
        description={t('cancelInviteConfirmDescription')}
        confirmLabel={t('cancelInviteConfirm')}
        cancelLabel={t('confirmCancel')}
        variant="danger"
      />
    </div>
  );
};
